export const BillingDayMode = Object.freeze({
  EffectiveDate: 'effective-date',
  Capped28: 'capped-28',
  Custom: 'custom'
});

// Not every month has a 29th–31st, so a policy effective on one of those days bills on the 28th.
export const LAST_BILLING_DAY = 28;

// The billing day a policy follows by default: its effective day, or the 28th for the 29th–31st.
export const getDefaultBillingDay = (effectiveDate) => {
  const effectiveDay = Number(effectiveDate?.slice(8, 10));
  return effectiveDay ? Math.min(effectiveDay, LAST_BILLING_DAY) : null;
};

/**
 * A policy's billing day of the month and how it was chosen, from its saved billing day and effective
 * date (`effectiveDate` is an AWSDate, 'yyyy-MM-dd'). With no saved billing day, billing follows the
 * effective day, so the policy reads as `effective-date` or `capped-28`. Returns null without an
 * effective date.
 *
 * shortcut: the mode is derived by comparing the two days, so it is approximate until #35596 stores
 * whether the agent picked the day. Until #35596 the default billing day is the effective day − 1
 * (data-defaults `getBillingDate`), so most offers read as `custom` even though nobody picked the day,
 * and a custom 28th on a policy effective on the 29th–31st reads as `capped-28`. Switch to the offer's
 * stored "billing day follows the effective date" flag once #35596 adds it.
 */
export const getBillingDay = ({ effectiveDate, billingDayOfMonth }) => {
  const defaultDay = getDefaultBillingDay(effectiveDate);
  if (!defaultDay) {
    return null;
  }
  const effectiveDay = Number(effectiveDate.slice(8, 10));
  const day = billingDayOfMonth ? Number(billingDayOfMonth) : defaultDay;

  let mode = BillingDayMode.EffectiveDate;
  if (day !== defaultDay) {
    mode = BillingDayMode.Custom;
  } else if (effectiveDay > LAST_BILLING_DAY) {
    mode = BillingDayMode.Capped28;
  }
  return { day, effectiveDay, mode };
};
